import { SwitchWithText } from '@adotel/components'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Button,
  Collapse,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core'
import FilterListIcon from '@material-ui/icons/FilterList'
import RefreshIcon from '@material-ui/icons/Refresh'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { addJob } from '../../cache/jobs'
import PageHeader from '../../components/PageHeader'
import {
  RATE_CONNECTION_LIST_QUERY,
  RATE_CONNECTION_UPDATE_MUTATION
} from '../../graphql/rateConnection'
import { DateObject, addDaysToDate, getTodayDate } from '../../helpers/date'
import {
  getCurrenciesAvailable,
  getDefaultCurrency,
  getEntityCode,
  getEntityId,
  getEntityName,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { JobsType } from '../../mockData/jobs'
import { ROLE } from '../../staticRoleManagement/types'
import { Currency } from '../../types/currency'
import {
  RateConnectionChannelFieldsType,
  RateConnectionList,
  RateConnectionListFilters,
  RateConnectionUpdateInput,
  RoomTypeForRateConnection
} from '../../types/rateConnection'
import PriceClassDistributionDateRow from './PriceClassDistribution'
import PriceClassDistributionRoomtypeBox from './PriceClassDistributionRoomtypeBox'
import PriceClassDistributionsBasicFilters from './PriceClassDistributionsBasicFilters'
import PriceClassDistributionsOptionalFilters from './PriceClassDistributionsOptionalFilters'
import PriceClassTableSkeleton from './PriceClassTable.skeleton'
import useStyles from './styles/index.styles'

type RangeType = {
  label: string
  value: 'TWO_WEEKS' | 'MONTH'
}

const RANGE_ITEMS: RangeType[] = [
  {
    label: getTranslate('View two weeks'),
    value: 'TWO_WEEKS'
  },
  {
    label: getTranslate('View monthly'),
    value: 'MONTH'
  }
]

export default function App() {
  const classes = useStyles()

  const isRoleProvider = getRoleFromStorage() === ROLE.PROVIDER

  const [autoCompleteForProvider, setAutoCompleteForProvider] = useState<any>(
    isRoleProvider
      ? {
          label: getEntityName(),
          value: getEntityId(),
          code: getEntityCode()
        }
      : null
  )
  const [autoCompleteForAccommodation, setAutoCompleteForAccommodation] =
    useState<any>(null)
  const [autoCompleteForRoomType, setAutoCompleteForRoomType] =
    useState<any>(null)
  const [autoCompleteForRatePlan, setAutoCompleteForRatePlan] =
    useState<any>(null)
  const [autoCompleteForChannel, setAutoCompleteForChannel] =
    useState<any>(null)
  const [autoCompleteForPriceClass, setAutoCompleteForPriceClass] =
    useState<any>(null)
  const [inputFilters, setInputFilters] = useState<RateConnectionListFilters>({
    providerCode: isRoleProvider ? getEntityCode() || '' : '',
    propertyCode: '',
    from: '',
    to: '',
    currency: getDefaultCurrency()
  })
  const [dateObject, setDateObject] = useState<DateObject>({
    gregorianStartDate: getTodayDate(),
    gregorianEndDate: addDaysToDate(getTodayDate(), 13)
  })
  const [showFilters, setShowFilters] = useState<boolean>(false)
  const [selectedRange, setSelectedRange] = useState<number>(0)
  const [selectedMenuFieldInChannelRow, setSelectedMenuFieldInChannelRow] =
    useState<RateConnectionChannelFieldsType>('rateMultiplier')

  const selectedDayInDatePickerWeekIndex = useMemo(
    () => moment(dateObject.gregorianStartDate).day(),
    [dateObject]
  )
  const weekendIndexes = useMemo(
    () => [
      selectedDayInDatePickerWeekIndex <= 4
        ? 4 - selectedDayInDatePickerWeekIndex
        : 11 - selectedDayInDatePickerWeekIndex,
      selectedDayInDatePickerWeekIndex <= 5
        ? 5 - selectedDayInDatePickerWeekIndex
        : 12 - selectedDayInDatePickerWeekIndex
    ],
    [dateObject]
  )

  const [
    getRateConnectionList,
    {
      data: rateConnectionListData,
      loading: loadingForRateConnectionList,
      refetch
    }
  ] = useLazyQuery<{ rateConnectionList: RateConnectionList }>(
    RATE_CONNECTION_LIST_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )
  const [updatedRateConnectionList, setUpdatedRateConnectionList] = useState<
    RoomTypeForRateConnection[]
  >([])

  useEffect(() => {
    if (rateConnectionListData?.rateConnectionList.roomTypes) {
      const temp: RoomTypeForRateConnection[] =
        rateConnectionListData?.rateConnectionList.roomTypes.map((rt) => ({
          ...rt,
          ratePlans: rt.ratePlans.map((rp) => ({
            ...rp,
            channels: rp.channels.map((ch) => ({
              ...ch,
              priceClasses: ch.priceClasses.map((pr) => ({
                ...pr,
                dataForEachDate: pr.dataForEachDate.map((d) => ({
                  ...d,
                  isChanged: false
                }))
              })),
              rateMultipliers: ch.rateMultipliers.map((rmp) => ({
                ...rmp,
                dataForEachDate: rmp.dataForEachDate.map((date) => ({
                  ...date,
                  isChanged: false
                }))
              }))
            }))
          }))
        }))

      setUpdatedRateConnectionList(temp)
    }
  }, [loadingForRateConnectionList])

  useEffect(() => {
    const temp: RoomTypeForRateConnection[] = updatedRateConnectionList.map(
      (rt, rtIndex) => ({
        ...rt,
        ratePlans: rt.ratePlans.map((rp, rpIndex) => ({
          ...rp,
          channels: rp.channels.map((ch, chIndex) => ({
            ...ch,
            priceClasses: ch.priceClasses.map((pr, prIndex) => ({
              ...pr,
              dataForEachDate: pr.dataForEachDate.map((d, dIndex) => ({
                ...d,
                isConnect:
                  rateConnectionListData?.rateConnectionList.roomTypes[rtIndex]
                    .ratePlans[rpIndex].channels[chIndex].priceClasses[prIndex]
                    .dataForEachDate[dIndex].isConnect || false,
                isChanged:
                  selectedMenuFieldInChannelRow === 'priceClass'
                    ? updatedRateConnectionList[rtIndex].ratePlans[rpIndex]
                        .channels[chIndex].priceClasses[prIndex]
                        .dataForEachDate[dIndex].isChanged
                    : false
              }))
            })),
            rateMultipliers: ch.rateMultipliers.map((rmp, rmpIndex) => ({
              ...rmp,
              dataForEachDate: rmp.dataForEachDate.map((date, dateIndex) => ({
                ...date,
                rateMultiplierValue:
                  rateConnectionListData?.rateConnectionList.roomTypes[rtIndex]
                    .ratePlans[rpIndex].channels[chIndex].rateMultipliers[
                    rmpIndex
                  ].dataForEachDate[dateIndex].rateMultiplierValue,
                isChanged:
                  selectedMenuFieldInChannelRow === 'rateMultiplier'
                    ? updatedRateConnectionList[rtIndex].ratePlans[rpIndex]
                        .channels[chIndex].rateMultipliers[rmpIndex]
                        .dataForEachDate[dateIndex].isChanged
                    : false
              }))
            }))
          }))
        }))
      })
    )

    setUpdatedRateConnectionList(temp)
  }, [selectedMenuFieldInChannelRow])

  const [updateConnectRates, { loading: loadingForUpdate }] = useMutation(
    RATE_CONNECTION_UPDATE_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (resp) => {
        const newJob: JobsType = {
          trackID: resp.rateConnectionUpdate.trackingID,
          creationDate: getTodayDate(),
          status: 'InProgress'
        }
        addJob(newJob)
        toast.info(
          `Rate Connection process with TrackingID ${resp.rateConnectionUpdate.trackingID} started`
        )
        refetch()
      },
      onError: (err) => toast.error(err.message)
    }
  )

  useEffect(() => {
    setInputFilters({
      ...inputFilters,
      from: dateObject.gregorianStartDate || '',
      to: dateObject.gregorianEndDate || ''
    })
  }, [dateObject.gregorianStartDate, dateObject.gregorianEndDate])

  useEffect(() => {
    if (
      inputFilters.providerCode &&
      inputFilters.propertyCode &&
      inputFilters.to
    )
      getRateConnectionList({
        variables: {
          input: inputFilters
        }
      })
  }, [inputFilters.propertyCode, inputFilters.to, inputFilters.currency])

  const handleChangeRange = (newRangeIndex: number) => {
    setSelectedRange(newRangeIndex)
    setDateObject({
      gregorianStartDate: dateObject.gregorianStartDate,
      gregorianEndDate: addDaysToDate(
        dateObject.gregorianStartDate!,
        RANGE_ITEMS[newRangeIndex].value === 'TWO_WEEKS' ? 13 : 30
      )
    })
  }

  const onClickOnApply = () => {
    const temp: RateConnectionUpdateInput[] = []
    const groupedInputs: { [key: string]: RateConnectionUpdateInput } = {}

    updatedRateConnectionList.forEach((roomType) => {
      roomType.ratePlans.forEach((ratePlan) => {
        ratePlan.channels.forEach((channel) => {
          // Handle PriceClass data
          channel.priceClasses.forEach((priceClass) => {
            priceClass.dataForEachDate.forEach((dateDetails) => {
              if (dateDetails.isChanged) {
                const key = `${roomType.id}-${ratePlan.id}-${channel.id}-${
                  dateDetails.isConnect
                }-${dateDetails.isConnect ? priceClass.Id : ''}`
                if (!groupedInputs[key]) {
                  groupedInputs[key] = {
                    providerCode: autoCompleteForProvider
                      ? autoCompleteForProvider.code
                      : '',
                    propertyCode: autoCompleteForAccommodation
                      ? autoCompleteForAccommodation.code
                      : '',
                    roomTypeCode: roomType.code,
                    ratePlanCode: ratePlan.code,
                    channelCode: channel.code,
                    date: [],
                    sub: dateDetails.isConnect,
                    priceClassID: dateDetails.isConnect
                      ? priceClass.Id
                      : undefined,
                    rateMultiplier: undefined
                  }
                }
                groupedInputs[key].date.push(dateDetails.date)
              }
            })
          })

          // Handle RateMultiplier data
          channel.rateMultipliers.forEach((rateMultiplier) => {
            rateMultiplier.dataForEachDate.forEach((dateDetails) => {
              if (dateDetails.isChanged) {
                const key = `${roomType.id}-${ratePlan.id}-${channel.id}-true-${rateMultiplier.name}-${dateDetails.date}-${dateDetails.rateMultiplierValue}`
                if (!groupedInputs[key]) {
                  groupedInputs[key] = {
                    providerCode: autoCompleteForProvider
                      ? autoCompleteForProvider.code
                      : '',
                    propertyCode: autoCompleteForAccommodation
                      ? autoCompleteForAccommodation.code
                      : '',
                    roomTypeCode: roomType.code,
                    ratePlanCode: ratePlan.code,
                    channelCode: channel.code,
                    date: [],
                    sub: true,
                    priceClassID: undefined,
                    rateMultiplier: dateDetails.rateMultiplierValue
                  }
                }
                groupedInputs[key].date.push(dateDetails.date)
              }
            })
          })
        })
      })
    })

    // Converting groupedInputs object back to an array
    for (const key in groupedInputs) {
      temp.push(groupedInputs[key])
    }

    updateConnectRates({
      variables: {
        input: temp
      }
    })
  }

  return (
    <div>
      <PageHeader title={getTranslate('Distribution')} />
      <SwitchWithText
        items={[RANGE_ITEMS[0].label, RANGE_ITEMS[1].label]}
        selectedItemIndex={selectedRange}
        onChange={handleChangeRange}
      />
      <PriceClassDistributionsBasicFilters
        autoCompleteForProvider={autoCompleteForProvider}
        setAutoCompleteForProvider={setAutoCompleteForProvider}
        autoCompleteForAccommodation={autoCompleteForAccommodation}
        setAutoCompleteForAccommodation={setAutoCompleteForAccommodation}
        inputFilters={inputFilters}
        setInputFilters={setInputFilters}
        isRoleProvider={isRoleProvider}
      />
      <Collapse
        in={
          !!autoCompleteForProvider &&
          !!autoCompleteForProvider.value &&
          !!autoCompleteForAccommodation &&
          !!autoCompleteForAccommodation.value
        }>
        <div>
          <div className={classes.currencyAndFiltersRow}>
            <div className={classes.currencyContainer}>
              <Typography>{getTranslate('Currency')}:</Typography>
              <Select
                value={inputFilters.currency}
                onChange={(event) => {
                  setInputFilters({
                    ...inputFilters,
                    currency: event.target.value as Currency
                  })
                }}>
                {getCurrenciesAvailable().map((currency) => (
                  <MenuItem
                    key={currency}
                    value={currency}
                    className={classes.menuItemRight}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className={classes.filtersButton}>
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => {
                  setShowFilters(!showFilters)
                }}
                variant={!showFilters ? 'contained' : 'outlined'}
                color="primary">
                {getTranslate('Filter')}
              </Button>
              <Button
                startIcon={<RefreshIcon />}
                className={classes.refreshButton}
                onClick={() => refetch()}
                variant="outlined"
                color="primary"
                disabled={loadingForRateConnectionList}>
                {getTranslate('Refresh')}
              </Button>
              <Button
                className={classes.refreshButton}
                onClick={onClickOnApply}
                variant="contained"
                color="primary"
                disabled={loadingForUpdate}>
                {getTranslate('Update')}
              </Button>
            </div>
          </div>
          <Collapse in={showFilters}>
            <div>
              <PriceClassDistributionsOptionalFilters
                autoCompleteForRoomType={autoCompleteForRoomType}
                setAutoCompleteForRoomType={setAutoCompleteForRoomType}
                autoCompleteForRatePlan={autoCompleteForRatePlan}
                setAutoCompleteForRatePlan={setAutoCompleteForRatePlan}
                autoCompleteForChannel={autoCompleteForChannel}
                setAutoCompleteForChannel={setAutoCompleteForChannel}
                autoCompleteForPriceClass={autoCompleteForPriceClass}
                setAutoCompleteForPriceClass={setAutoCompleteForPriceClass}
                propertyCode={
                  autoCompleteForAccommodation &&
                  autoCompleteForAccommodation.code
                }
                inputFilters={inputFilters}
                setInputFilters={setInputFilters}
                getRateConnectionList={getRateConnectionList}
                loadingForRateConnectionList={loadingForRateConnectionList}
                isRoleProvider={isRoleProvider}
              />
            </div>
          </Collapse>
        </div>
        <div>
          <PriceClassDistributionDateRow
            selectedDateInDatePicker={dateObject}
            setSelectedDateInDatePicker={setDateObject}
            range={RANGE_ITEMS[selectedRange].value}
          />
          {loadingForRateConnectionList || loadingForUpdate ? (
            <PriceClassTableSkeleton />
          ) : (
            updatedRateConnectionList.map(
              (item: RoomTypeForRateConnection, index) => (
                <PriceClassDistributionRoomtypeBox
                  key={index}
                  roomTypeData={item}
                  roomTypeIndex={index}
                  updatedRateConnectionList={updatedRateConnectionList}
                  setUpdatedRateConnectionList={setUpdatedRateConnectionList}
                  weekendIndexes={weekendIndexes}
                  range={RANGE_ITEMS[selectedRange].value}
                  selectedMenuFieldInChannelRow={selectedMenuFieldInChannelRow}
                  setSelectedMenuFieldInChannelRow={
                    setSelectedMenuFieldInChannelRow
                  }
                />
              )
            )
          )}
        </div>
      </Collapse>
    </div>
  )
}
